'use strict';

define('vb/private/stateManagement/context/extensionContext',[
  'vb/private/stateManagement/context/containerContext',
], (ContainerContext) => {
  const symbol = Symbol('extension-accessor');
  /**
   * Set of properties common for pageExtension and flowExtension
   */
  class ExtensionContext extends ContainerContext {
    /**
     * @param extension the extension container
     */
    constructor(extension) {
      super(extension);

      Object.defineProperty(this, symbol,
        {
          value: {
            extension,
            get application() {
              const appExpressionContext = {};
              const extensionApplication = extension.base.application.extensions[extension.extensionId];
              if (extensionApplication) {
                // For every extension container, the application object is a combination of the properties
                // in the application extension and the properties exposed in the base application interface
                const extensionExpressionContext = extensionApplication.expressionContext;
                const baseExpressionContext = extensionExpressionContext.base;

                // variables, constants and events are the only properties in interface
                ['variables', 'constants', 'events'].forEach((scope) => {
                  appExpressionContext[scope] = {};

                  let ctx = extensionExpressionContext[scope] || [];
                  Object.keys(ctx).forEach((name) => {
                    const def = Object.getOwnPropertyDescriptor(ctx, name);
                    Object.defineProperty(appExpressionContext[scope], name, def);
                  });

                  appExpressionContext.getVariable = (...args) => {
                    return extensionApplication.expressionContext.getVariable(...args);
                  };

                  ctx = baseExpressionContext[scope] || [];
                  Object.keys(ctx).forEach((name) => {
                    const def = Object.getOwnPropertyDescriptor(ctx, name);
                    Object.defineProperty(appExpressionContext[scope], name, def);
                  });
                });
              }

              // and redefine the property as a value for immediate access on the next get
              Object.defineProperty(this, 'application', {
                value: appExpressionContext,
                enumerable: true,
              });

              return appExpressionContext;
            },
          },
        });
    }

    /**
     * BaseContextType property: The type of the baseContext object used for $base in expression.
     * This is used by super class ContainerContext to construct the baseContext object.
     *
     * Implemented by subclass pageExtensionContext and flowExtensionContext
     */
    static get BaseContextType() {
      throw new Error('Base context constructor indefined');
    }

    /**
     * base property
     * The object returned when an expression has $base (See $base property in getAvailableContexts)
     * @return {BaseContext}
     */
    get base() {
      return this[symbol].extension.base.expressionContext.baseContext;
    }

    /**
     * extension property
     * The object returned when an expression has $extension (See $extension property in getAvailableContexts)
     * @return {BaseContext}
     */
    get extension() {
      return {
        base: this.base,
        path: this[symbol].extension.absoluteUrl,
      };
    }

    /**
     * Accessor for the application
     * @return {Object}
     */
    get application() {
      return this[symbol].application;
    }

    /**
     * see ContainerContext
     *
     * @param extension
     * @returns {{$variables, $base.variables, ...}}
     */
    static getAvailableContexts(extension) {
      // This part populate $variables, $constants, etc...
      const availableContexts = super.getAvailableContexts(extension);

      Object.defineProperties(availableContexts, {
        $extension: {
          enumerable: true,
          configurable: true,
          get: () => extension.expressionContext.extension,
        },
        $base: {
          enumerable: true,
          configurable: true,
          get: () => extension.expressionContext.base,
        },
      });

      return availableContexts;
    }
  }

  return ExtensionContext;
});

